import cx from 'classnames'

import { PaymentPlan } from 'api/clients/PaymentPlanClient'
import { Spinner } from 'common/Spinner/Spinner'
import { BillingFrequency } from 'components/Paywall/UpgradePlanModal/UpgradePlanModal'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useEligiblePlan } from 'hooks/useEligiblePlan'
import { FREE_PLAN } from 'hooks/usePaymentPlans'
import { Modal } from 'almanac-ui/Modal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { BillingFrequencyRadioButtons } from '../BillingFrequencyRadioButtons/BillingFrequencyRadioButtons'
import { ContactInformation } from '../ContactInformation/ContactInformation'
import { HeaderContent as BlazeHeaderContent } from '../HeaderContent/Blaze/HeaderContent'
import Styles from './SelectPlan.module.scss'
import { PlanDetail } from './components/PlanDetail/PlanDetail'

interface Props {
  availablePlans: PaymentPlan[]
  billingFrequency: BillingFrequency
  onClose: () => void
  savedPlan: PaymentPlan
  selectPlan: (plan: PaymentPlan) => void
  setBillingFrequency: (frequency: BillingFrequency) => void
  showWorkspacesLink?: boolean
  isCanceled?: boolean
}

export const SelectPlan = ({
  availablePlans,
  billingFrequency,
  onClose,
  savedPlan,
  selectPlan,
  setBillingFrequency,
  showWorkspacesLink = false,
  isCanceled = false,
}: Props) => {
  const isEligible = useEligiblePlan()
  const workspace = useCurrentWorkspace()
  const { isMobile } = useResponsiveDesign()

  return (
    <>
      <BlazeHeaderContent
        billingFrequency={billingFrequency}
        setBillingFrequency={setBillingFrequency}
        onClose={onClose}
        showWorkspacesLink={showWorkspacesLink}
      />
      {workspace?.subscription_provider === 'apple' ? (
        <Modal.Content>
          <div className={Styles.subheader}>
            {!workspace?.paying && (
              <p className={Styles.subtitle}>
                Your subscription is currently managed through the Blaze app on your iPhone or iPad. To manage your
                plan, please open the Blaze app on your mobile device.
              </p>
            )}
          </div>
        </Modal.Content>
      ) : (
        <Modal.Content>
          <div className={Styles.subheader}>
            {!workspace?.paying && (
              <p className={Styles.subtitle}>Subscribe to one of our plans to keep using Blaze.</p>
            )}
            {isMobile && workspace?.paying && (
              <BillingFrequencyRadioButtons
                billingFrequency={billingFrequency}
                setBillingFrequency={setBillingFrequency}
              />
            )}
          </div>
          {availablePlans.length > 0 && (
            <div
              className={cx(Styles.planContent, {
                [Styles.loading]: availablePlans.length === 0,
              })}
            >
              {availablePlans.map((plan, index) => {
                return (
                  <PlanDetail
                    key={`${plan}-${index}`}
                    billingFrequency={billingFrequency}
                    savedPlan={savedPlan}
                    plan={plan}
                    selectPlan={selectPlan}
                    isDisabled={!isEligible(plan)}
                    isCanceled={savedPlan.key === plan.key && isCanceled}
                  />
                )
              })}
            </div>
          )}
          {availablePlans.length === 0 && (
            <div className={Styles.loadingContainer}>
              <Spinner />
            </div>
          )}
          <div className={Styles.contactInformationContainer}>
            <ContactInformation />
          </div>
        </Modal.Content>
      )}
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          {workspace?.paying && !isCanceled && !workspace.collection_paused && (
            <Modal.FooterButton size="lg" variant="secondary" onPress={() => selectPlan(FREE_PLAN)}>
              Cancel Subscription
            </Modal.FooterButton>
          )}
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
